import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { PodcastEpisode, EpisodeStatus } from '@/types/episode.types';
import { PodcastEpisodeService } from '@/services/PodcastEpisodeService';
import { MobileEpisodeCard } from './MobileEpisodeCard';
import { MobileSummaryCategory } from './MobileContainer';

interface MobileEpisodeListProps {
  statusFilter: MobileSummaryCategory;
  onEpisodeSelect: (episodeId: string) => void;
  onBack: () => void;
}

// Map mobile summary categories to episode statuses
const CATEGORY_STATUS_MAP: Record<MobileSummaryCategory, EpisodeStatus[]> = {
  needsReview: [
    EpisodeStatus.SCRIPTS_PENDING_REVIEW,
    EpisodeStatus.AUDIO_PENDING_REVIEW,
  ],
  ready: [
    EpisodeStatus.MERGED,
    EpisodeStatus.AUDIO_APPROVED,
  ],
  published: [
    EpisodeStatus.PUBLISHED,
  ],
  failed: [
    EpisodeStatus.FAILED,
    EpisodeStatus.SCRIPT_FAILED,
    EpisodeStatus.AUDIO_FAILED,
    EpisodeStatus.PUBLISHING_FAILED,
  ],
};

const CATEGORY_LABELS: Record<MobileSummaryCategory, string> = {
  needsReview: 'Needs Review',
  ready: 'Ready to Publish',
  published: 'Published',
  failed: 'Failed',
};

const ITEMS_PER_PAGE = 10;

export const MobileEpisodeList: React.FC<MobileEpisodeListProps> = ({
  statusFilter,
  onEpisodeSelect,
  onBack,
}) => {
  const [episodes, setEpisodes] = useState<PodcastEpisode[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const loadEpisodes = useCallback(async (pageNum: number, append: boolean = false) => {
    const isFirstLoad = pageNum === 1 && !append;
    if (isFirstLoad) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const result = await PodcastEpisodeService.getEpisodesPaginated({
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        orderBy: 'updatedAt',
        order: 'desc',
      });

      // Filter episodes by status client-side
      const statusesToInclude = CATEGORY_STATUS_MAP[statusFilter];
      const filteredEpisodes = result.episodes.filter(ep =>
        statusesToInclude.includes(ep.status) ||
        (statusFilter === 'failed' && ep.failedClipCount && ep.failedClipCount > 0)
      );

      if (append) {
        setEpisodes(prev => [...prev, ...filteredEpisodes]);
      } else {
        setEpisodes(filteredEpisodes);
      }

      // Check if there are more pages
      setHasMore(result.meta.page < result.meta.totalPages);
    } catch (err) {
      console.error('Error loading episodes:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    setPage(1);
    loadEpisodes(1, false);
  }, [statusFilter, loadEpisodes]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadEpisodes(nextPage, true);
  };

  const handleRefresh = () => {
    setPage(1);
    loadEpisodes(1, false);
  };

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-6 w-32" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b bg-background sticky top-0 z-10">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={onBack}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-semibold">{CATEGORY_LABELS[statusFilter]}</h1>
          <p className="text-xs text-muted-foreground">
            {episodes.length} episode{episodes.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={handleRefresh}
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Episode List */}
      <div className="flex-1 overflow-auto p-4 space-y-3">
        {episodes.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No episodes found</p>
          </div>
        ) : (
          <>
            {episodes.map((episode) => (
              <MobileEpisodeCard
                key={episode.id}
                episode={episode}
                onClick={() => onEpisodeSelect(episode.id)}
              />
            ))}

            {/* Load More Button */}
            {hasMore && (
              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load More'
                )}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
